import React from "react";
import { NotificationManager } from "react-notifications";
import { useDataView } from "../../contexts/data.view.context";
import * as API from "../api/index";
import "../util/tables.css";
import ApiConfigTable from "./apiConfigView";
import { sliceByEnv, getEnvColumnValue, getEnvRow, getRowDiff, mergeFields } from "../shared/envUtils";
import ShowLoadingBar from "../util/showLoadingBar";
import { makeEnvCellRenderer } from "../shared/makeEnvCellRenderer";
import SyncDialog from "../util/confirmMatchDialog";
import viewDetailsModal from "./viewDetailsModal";
import { useFetchOnMount } from "../../hooks/useFetchOnMount";

const APIConfigs = () => {
  const {
    apiConfigs,
    fetchAPIConfigsFromAllEnv,
    REACT_APP_ENV,
    envColumn1,
    envColumn2,
    openPromoteModal,
    setOpenPromoteModal,
    envCell,
    handleClickOpen,
    handleClose,
    openMatchModal,
    setOpenMatchModal,
    matchCell,
    matchSelectedFields,
    setMatchSelectedFields,
    matchDirection,
    setMatchDirection,
    handleMatchClickOpen,
    handleMatchClose,
  } = useDataView();

  let detailData = "";
  let tableData = "";
  let currentEnvAPIConfig = [];
  let envSlice = { current: [], dev: [], test: [], prod: [] };

  const envCellRenderer = makeEnvCellRenderer({
    openPromoteModal, envCell, handleClickOpen, handleClose, handleConfirm: (...args) => handleConfirmOnPromote(...args),
    handleMatchClickOpen,
  });

  const mainColumns = [
    {
      accessorKey: "API_ID",
      header: "Api id",
      size: 275,
      Cell: viewDetailsModal({ detailData }),
    },
    {
      accessorKey: "MOD",
      header: "Mod",
      size: 75,
    },
    {
      accessorKey: "STATE",
      header: "State",
      size: 150,
    },
    {
      accessorKey: "envColumn1",
      header: envColumn1,
      id: envColumn1,
      size: 150,
      Cell: envCellRenderer,
    },
    {
      accessorKey: "envColumn2", //access nested data with dot notation
      header: envColumn2,
      id: envColumn2,
      size: 150,
      Cell: envCellRenderer,
    },
  ];

  const handleConfirmOnMatch = (fields, direction) => {
    const current = matchCell.current && matchCell.current.cell.row.original;
    const target = matchCell.current && matchCell.current.targetRow;

    if (!current || !target) {
      setOpenMatchModal(false);
      return;
    }

    // _API_ID (leading underscore) is required by the backend upsert to
    // take the UPDATE branch instead of silently INSERTing a duplicate —
    // see mergeFields below: it's never in `fields`, so it always comes
    // from whichever row (current/target) is actually being written to.
    const fieldNames = ["API_ID", "MOD", "PATH", "OPERATION", "STATE", "NAME", "REF_TYPE", "REF_ID", "SECURITY_POLICY", "SAMPLE_DATA", "REF_DATA", "_API_ID"];
    const isPush = direction === "push";
    // Pull: base = current record, override checked fields with target's values, write back to current env.
    // Push: base = target record, override checked fields with current's values, write to target env.
    const merged = isPush
      ? mergeFields(fieldNames, target, current, fields)
      : mergeFields(fieldNames, current, target, fields);
    const destEnv = isPush ? matchCell.current.cell.column.id : REACT_APP_ENV;

    onSubmit([merged], destEnv);
    setOpenMatchModal(false);
  };

  const handleConfirmOnPromote = () => {
    const payload = [
      {
        API_ID: envCell.current.row.original.API_ID,
        MOD: envCell.current.row.original.MOD,
        PATH: envCell.current.row.original.PATH,
        OPERATION: envCell.current.row.original.OPERATION,
        STATE: "Pending",
        NAME: envCell.current.row.original.NAME,
        REF_TYPE: envCell.current.row.original.REF_TYPE,
        REF_ID: envCell.current.row.original.REF_ID,
        SECURITY_POLICY: envCell.current.row.original.SECURITY_POLICY,
        SAMPLE_DATA: envCell.current.row.original.SAMPLE_DATA,
        REF_DATA: envCell.current.row.original.REF_DATA,
      },
    ];

    const env = envCell.current.column.id;

    onSubmit(payload, env);
    setOpenPromoteModal(false);
  };

  const onSubmit = (payload, env) => {
    const resourcePath = "integrations/cfg/apiconfigs";

    API.AddRecordToOtherEnv(resourcePath, env, payload).then((response) => {
      if (response.code === 200) {
        NotificationManager.success(
          "Record added to " + env + " env",
          "API Config",
          10000
        );
        fetchAPIConfigsFromAllEnv();
      } else {
        NotificationManager.error(response.message, "Failed!", 10000);
      }
    });
  };
  useFetchOnMount(apiConfigs, fetchAPIConfigsFromAllEnv);

  if (apiConfigs.length > 0) {
    envSlice = sliceByEnv(apiConfigs, REACT_APP_ENV);
    currentEnvAPIConfig = envSlice.current;

    detailData = currentEnvAPIConfig.map((apiConfig) => {
      return {
        _API_ID: apiConfig._API_ID,
        API_ID: apiConfig.API_ID,
        MOD: apiConfig.MOD,
        PATH: apiConfig.PATH,
        OPERATION: apiConfig.OPERATION,
        STATE: apiConfig.STATE,
        NAME: apiConfig.NAME,
        REF_TYPE: apiConfig.REF_TYPE,
        REF_ID: apiConfig.REF_ID,
        SECURITY_POLICY: apiConfig.SECURITY_POLICY,
        SAMPLE_DATA: apiConfig.SAMPLE_DATA,
        REF_DATA: apiConfig.REF_DATA,
      };
    });

    tableData = currentEnvAPIConfig.map((apiConfig) => {
      const col1 = getEnvColumnValue({ envColumn: envColumn1, ...envSlice, idKey: "_API_ID", idValue: apiConfig._API_ID });
      const col2 = getEnvColumnValue({ envColumn: envColumn2, ...envSlice, idKey: "_API_ID", idValue: apiConfig._API_ID });
      const targetRow1 = getEnvRow({ envColumn: envColumn1, ...envSlice, idKey: "_API_ID", idValue: apiConfig._API_ID });
      const targetRow2 = getEnvRow({ envColumn: envColumn2, ...envSlice, idKey: "_API_ID", idValue: apiConfig._API_ID });

      return {
        _API_ID: apiConfig._API_ID,
        API_ID: apiConfig.API_ID,
        MOD: apiConfig.MOD,
        PATH: apiConfig.PATH,
        OPERATION: apiConfig.OPERATION,
        STATE: apiConfig.STATE,
        NAME: apiConfig.NAME,
        REF_TYPE: apiConfig.REF_TYPE,
        REF_ID: apiConfig.REF_ID,
        SECURITY_POLICY: apiConfig.SECURITY_POLICY,
        SAMPLE_DATA: apiConfig.SAMPLE_DATA,
        REF_DATA: apiConfig.REF_DATA,
        envColumn1: col1,
        envColumn2: col2,
        [`${envColumn1}__diff`]: { ...getRowDiff(apiConfig, targetRow1), targetRow: targetRow1 },
        [`${envColumn2}__diff`]: { ...getRowDiff(apiConfig, targetRow2), targetRow: targetRow2 },
      };
    });
  }

  let loading = false;
  if (currentEnvAPIConfig.length === 0) {
    loading = true;
  }

  if (Boolean(loading)) {
    return <ShowLoadingBar title="LIST of API CONFIGS" loading={loading} />;
  }

  return (
    <div className="cache-table">

      {currentEnvAPIConfig.length > 0 && (
        <ApiConfigTable
          data={tableData}
          mainColumns={mainColumns}
          autoResetPageIndex={false}
        />
      )}

      <SyncDialog
        open={openMatchModal}
        matchCell={matchCell}
        matchSelectedFields={matchSelectedFields}
        setMatchSelectedFields={setMatchSelectedFields}
        matchDirection={matchDirection}
        setMatchDirection={setMatchDirection}
        handleMatchClose={handleMatchClose}
        handleMatchConfirm={(...args) => handleConfirmOnMatch(...args)}
      />
    </div>
  );
};

export default APIConfigs;
